import { StyledButtonGroup } from '@app/components/button-group/ButtonGroup';
import { StyledButton } from '@app/components/button/Button';
import { TextField } from '@app/components/form-fields/TextField';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import type { UseFormReset } from 'react-hook-form';
import { Controller, useForm } from 'react-hook-form';
import * as z from 'zod';
import PasswordValidationRules from './PasswordValidationRules';

export const formSchema = z
    .object({
        password: z.string().trim().min(1, 'Password is required'),
        passwordConfirmation: z.string().trim().min(1, 'Confirm password is required'),
    })
    .refine((data) => data.password === data.passwordConfirmation, {
        message: "Passwords don't match",
        path: ['passwordConfirmation'], // attaches error to the confirm field
    });

export type ResetPasswordFormValues = z.infer<typeof formSchema>;

export type ResetPasswordFormProps = {
    onSubmit: (
        data: ResetPasswordFormValues,
        reset?: UseFormReset<ResetPasswordFormValues>
    ) => void;
};

export const ResetPasswordForm = ({ onSubmit }: ResetPasswordFormProps) => {
    const {
        handleSubmit,
        control,
        reset,
        watch,
        formState: { isValid, isSubmitting },
    } = useForm<ResetPasswordFormValues>({
        mode: 'all',
        resolver: zodResolver(formSchema),
        defaultValues: {
            password: '',
            passwordConfirmation: '',
        },
    });

    const watchedPassword = watch('password');
    const watchedPasswordConfirmation = watch('passwordConfirmation');

    const [isPasswordValid, setIsPasswordValid] = useState(false);

    return (
        <form onSubmit={handleSubmit((values) => onSubmit(values, reset))}>
            <div className="form-fields-wrapper">
                <Controller
                    name="password"
                    control={control}
                    render={({ field, fieldState }) => (
                        <TextField
                            fieldState={fieldState}
                            {...field}
                            label={
                                <span className="label-with-action">
                                    <span>Password</span>
                                </span>
                            }
                            type="password"
                            placeholder="Enter your Password"
                            showViewPasswordIcon
                        />
                    )}
                />

                <Controller
                    name="passwordConfirmation"
                    control={control}
                    render={({ field, fieldState }) => (
                        <TextField
                            fieldState={fieldState}
                            {...field}
                            label={<span>Confirm Password</span>}
                            type="password"
                            placeholder="Re-enter your Password"
                            showViewPasswordIcon
                        />
                    )}
                />

                <PasswordValidationRules
                    password={watchedPassword}
                    passwordConfirmation={watchedPasswordConfirmation}
                    checkPasswordValidity={setIsPasswordValid}
                />
            </div>

            <StyledButtonGroup>
                <StyledButton
                    htmlType="submit"
                    type="primary"
                    disabled={!isValid || !isPasswordValid}
                    loading={isSubmitting}>
                    Submit
                </StyledButton>
            </StyledButtonGroup>
        </form>
    );
};
